import { useQuery } from '@tanstack/react-query';
import { API, Auth } from 'aws-amplify';
import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { formatAllianceDate } from './alliance/app-banners';
import { SystemIdentity, SystemSplash } from './alliance/system-screen';
import {
  AccessDeniedScreen,
  AccountInactiveScreen,
  AccountPendingScreen,
  AuthFailScreen,
  MaintenanceScreen,
  MfaRequiredScreen,
  NotAuthorizedScreen,
  SessionExpiredScreen,
} from './alliance/system-screens';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { AuthorizeData } from '../types';
import { captureError } from '../utils/capture-error';
import { checkForImpersonatedUser } from '../utils/impersonate';
import { checkForRedirectPath, storeRedirectPath } from '../utils/redirect';

/** The shape of an Amplify API (axios) error, as far as the gate needs it */
interface ApiError {
  message?: string;
  response?: {
    status?: number;
    data?: {
      message?: string;
      errors?: { msg?: string | null }[];
      data?: { UserStatus?: string };
    };
  };
}

/**
 * True when an error means the Cognito session is gone (refresh token
 * expired or revoked). Amplify sometimes rejects with a plain string.
 */
const isSessionError = (error: unknown) => {
  const err = error as (ApiError & { code?: string; name?: string }) | null;
  if (err?.response?.status === 401) {
    return true;
  }
  const text = `${err?.code || ''} ${err?.name || ''} ${
    err?.message || (typeof error === 'string' ? error : '')
  }`;
  return /not authenticated|no current user|NotAuthorizedException|refresh token|token has expired|token has been revoked/i.test(
    text,
  );
};

/** The API's own message from an error response, when it sent one */
const getServerMessage = (error: unknown) => {
  const data = (error as ApiError | null)?.response?.data;
  return data?.errors?.find((item) => item.msg)?.msg || data?.message || null;
};

/** Agent statuses that block the app, from /authorize Agent.AgtStatus */
type BlockedStatus = 'pending' | 'inactive' | 'terminated';

const toBlockedStatus = (
  value: string | null | undefined,
): BlockedStatus | null => {
  switch ((value || '').toLowerCase()) {
    case 'pending':
      return 'pending';
    case 'inactive':
      return 'inactive';
    case 'terminated':
      return 'terminated';
    default:
      return null;
  }
};

/**
 * The gate in front of the signed-in shell. In order:
 *
 * 1. Maintenance (agents only; admins get the banner in the shell)
 * 2. Splash while the session, /authorize and MFA checks load
 * 3. Session expired, when Cognito says the session is gone. This is
 *    checked before MFA, so a dead session never lands on the MFA screen
 * 4. MFA not set up (only when the MFA check succeeded and said so)
 * 5. /authorize errors: 403 with UserStatus Inactive/Pending/Terminated, other
 *    403s (not authorized), anything else (couldn't load, with Try again)
 * 6. Agent status from /authorize: Pending, Inactive, Terminated
 */
export function RequireAuth(props: {
  children: JSX.Element;
  isAdmin: boolean;
  maintData:
    | {
        maintenanceActive: boolean;
        maintenanceBegin: string;
        maintenanceEnd: string;
      }
    | null
    | undefined;
}) {
  // Context
  const { state, dispatch } = React.useContext(AuthContext);
  // State
  const [fromCognito] = React.useState(() =>
    new URLSearchParams(window.location.search).has('code'),
  );
  // Hooks
  const navigate = useNavigate();
  const identity = useAllianceIdentity();
  const { signOut, signInAgain } = useSessionActions();

  React.useEffect(() => {
    const handleCheckForPath = async () => {
      try {
        const redirectPath = await checkForRedirectPath();
        if (redirectPath) {
          navigate(redirectPath);
        }
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    const handleCheckForImpersonatedUser = async () => {
      try {
        const impersonatedUser = await checkForImpersonatedUser();
        if (impersonatedUser) {
          dispatch({ type: 'IMPERSONATE_AGENT', payload: impersonatedUser });
        }
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    const handleStorePath = async () => {
      try {
        if (window?.location?.pathname) {
          const { pathname } = window.location;
          // If the user was trying to access a route other than the main route
          if (pathname !== '/') {
            let path = pathname;
            // If there are any search parameters add them to the redirect path
            if (window?.location?.search) {
              path = path + window.location.search;
            }
            // Add the route to storage for use after logging in
            await storeRedirectPath({ pathname: path });
          }
        }
      } catch (error) {
        captureError({ data: { error } });
      }
    };

    if (state.user !== undefined) {
      // Check for stored path
      handleCheckForPath();
      // Check for stored impersonated user
      handleCheckForImpersonatedUser();
    } else {
      // Store path
      handleStorePath();
    }
  }, [state.user, navigate, dispatch]);

  const isAuthenticated = state.user !== undefined;

  // Query - Authorize User
  const pathAuth = '/authorize';
  const queryAuth = useQuery({
    enabled: isAuthenticated,
    queryKey: [pathAuth],
    queryFn: async () => {
      const response: {
        data: AuthorizeData;
      } = await API.post('LeadsAPI', pathAuth, {});

      if (response.data) {
        dispatch({ type: 'SET_AUTHORIZE', payload: response.data });
      }

      return response.data;
    },
  });

  // Query - Check that the user has set up Multi-Factor Authentication for their account
  const queryMFA = useQuery({
    enabled: isAuthenticated,
    queryKey: ['check_Auth.getPreferredMFA'],
    queryFn: async () => {
      // Bypass Cache to always get the latest preferences for the Current User
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      const preferredMFA = await Auth.getPreferredMFA(currentUser);

      const mfaOptions = ['EMAIL_OTP', 'SMS_MFA', 'SOFTWARE_TOKEN_MFA'];

      // Determine if the user has selected one of the available options
      return mfaOptions.includes(preferredMFA || '');
    },
  });

  const isLoadingMFA = queryMFA.isLoading && queryMFA.fetchStatus !== 'idle';

  // "Signed in as" on the full-page screens: name + number once /authorize
  // has answered, otherwise just the Cognito username
  let systemIdentity: SystemIdentity | null = null;
  if (identity && state.authorize) {
    systemIdentity = {
      kind: 'full',
      name: identity.user.fullName,
      id: identity.user.id,
    };
  } else if (state.user) {
    systemIdentity = { kind: 'token', id: state.user.getUsername() };
  }

  const handleRetry = () => {
    queryAuth.refetch();
    queryMFA.refetch();
  };

  const authStatus = (queryAuth.error as ApiError | null)?.response?.status;
  const authUserStatus = toBlockedStatus(
    (queryAuth.error as ApiError | null)?.response?.data?.data?.UserStatus,
  );
  const agentStatus = toBlockedStatus(state.authorize?.Agent?.AgtStatus);
  // An inactive staff/employee record on /authorize
  const staffInactive = state.authorize?.Staff?.Active === '0';
  const blockedStatus =
    (queryAuth.isError && authStatus === 403 ? authUserStatus : null) ||
    (queryAuth.isSuccess ? agentStatus : null) ||
    (queryAuth.isSuccess && staffInactive ? 'inactive' : null);

  const sessionExpired =
    (queryMFA.isError && isSessionError(queryMFA.error)) ||
    (queryAuth.isError && isSessionError(queryAuth.error)) ||
    (state.tokenError && (queryAuth.isError || queryMFA.isError));

  if (!props.isAdmin && props.maintData && props.maintData.maintenanceActive) {
    // Display a Maintenance Message to agents
    return (
      <MaintenanceScreen
        until={formatAllianceDate({
          value: props.maintData.maintenanceEnd,
          long: true,
        })}
        identity={systemIdentity}
        onSignOut={isAuthenticated ? signOut : undefined}
      />
    );
  } else if (state.isChecking || queryAuth.isFetching || isLoadingMFA) {
    return (
      <SystemSplash caption={fromCognito ? 'Signing you in…' : undefined} />
    );
  } else if (isAuthenticated && sessionExpired) {
    return <SessionExpiredScreen onSignIn={signInAgain} />;
  } else if (isAuthenticated && queryMFA.isError) {
    // The MFA check failed for another reason: that isn't "no MFA"
    return (
      <AuthFailScreen
        identity={systemIdentity}
        onRetry={handleRetry}
        onSignOut={signOut}
      />
    );
  } else if (isAuthenticated && queryMFA.isSuccess && !queryMFA.data) {
    return <MfaRequiredScreen identity={systemIdentity} onSignOut={signOut} />;
  } else if (blockedStatus === 'pending') {
    return (
      <AccountPendingScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (blockedStatus === 'inactive') {
    return (
      <AccountInactiveScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (blockedStatus === 'terminated') {
    return <AccessDeniedScreen onSignOut={signOut} />;
  } else if (queryAuth.isError && authStatus === 403) {
    return (
      <NotAuthorizedScreen identity={systemIdentity} onSignOut={signOut} />
    );
  } else if (queryAuth.isError) {
    return (
      <AuthFailScreen
        identity={systemIdentity}
        onRetry={handleRetry}
        onSignOut={signOut}
        serverMessage={getServerMessage(queryAuth.error)}
      />
    );
  } else if (isAuthenticated) {
    return props.children;
  } else {
    return <Navigate to="/sign-in" replace />;
  }
}
